import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import { Flame } from "lucide-react";

/**
 * Cycle Completion Modal - 100 Tasks
 * 
 * Reflective, earned, slightly elevated.
 * Not louder than 50—deeper than 50.
 * Feels like: completion + quiet pride + continuation
 * 
 * Cycle 9 is a special moment - "Nine completes the cycle"
 */

// Soft warm resolution tone - longer decay than 50
const playResolutionSound = (isCycle9 = false) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const playTone = (time, freq, volume, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(freq, time);
      oscillator.frequency.exponentialRampToValueAtTime(freq * 0.65, time + duration);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, time);
      gainNode.gain.linearRampToValueAtTime(volume, time + 0.04);
      gainNode.gain.exponentialRampToValueAtTime(0.001, time + duration);
      
      oscillator.start(time);
      oscillator.stop(time + duration + 0.1);
    };
    
    const now = audioContext.currentTime;
    
    if (isCycle9) {
      // Cycle 9: Soft, warm cinematic tone with longer decay
      playTone(now, 98, 0.055, 0.85);        // Deeper base
      playTone(now + 0.04, 147, 0.05, 0.75); // Rich mid
      playTone(now + 0.1, 196, 0.045, 0.7);  // Warm high
      playTone(now + 0.18, 262, 0.03, 0.6);  // Gentle sparkle
    } else {
      // Standard: Warm resolution chord
      playTone(now, 110, 0.06, 0.6);
      playTone(now + 0.03, 165, 0.05, 0.55);
      playTone(now + 0.08, 220, 0.04, 0.5);
    }
    
  } catch (e) {
    // Audio not supported
  }
};

// Three-stage heartbeat: soft → deeper → soft (feels like closure)
const triggerClosureHaptic = (isCycle9 = false) => {
  if ('vibrate' in navigator) {
    if (isCycle9) {
      // Cycle 9: Deep, calm multi-stage pulse - feels like arrival
      navigator.vibrate(60);
      setTimeout(() => navigator.vibrate(95), 200);  // Deeper
      setTimeout(() => navigator.vibrate(70), 420);  // Settling
      setTimeout(() => navigator.vibrate(40), 600);  // Final rest
    } else {
      // Standard: Three-stage heartbeat
      navigator.vibrate(50);
      setTimeout(() => navigator.vibrate(85), 180);
      setTimeout(() => navigator.vibrate(45), 380);
    }
  }
};

// Generate elegant particles
const generateCompletionParticles = (isCycle9 = false) => {
  // Cycle 9: Slightly more density than earlier milestones
  const count = isCycle9 ? 26 : 20;
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    angle: (Math.PI * 2 * i) / count + (Math.random() - 0.5) * (isCycle9 ? 0.25 : 0.35),
    distance: isCycle9 ? (95 + Math.random() * 55) : (90 + Math.random() * 60),
    size: isCycle9 ? (2.5 + Math.random() * 3) : (3 + Math.random() * 3.5),
    delay: isCycle9 ? (0.5 + (i * 0.035)) : (0.4 + (i * 0.04)),
    duration: isCycle9 ? (1.4 + Math.random() * 0.35) : (1.2 + Math.random() * 0.4),
    color: [
      'rgba(249, 115, 22, 0.5)',   // Orange
      'rgba(251, 146, 60, 0.45)',  // Light orange
      'rgba(239, 68, 68, 0.4)',    // Red
      'rgba(251, 191, 36, 0.45)',  // Amber
      'rgba(254, 215, 170, 0.4)',  // Peach
      'rgba(253, 186, 116, 0.45)', // Soft orange
      'rgba(220, 38, 38, 0.35)',   // Deep red
    ][Math.floor(Math.random() * 7)],
  }));
};

export const CycleCompletionModal = ({ isOpen, onClose, onCycleReset, cycleCount = 1, totalCompleted = 0 }) => {
  const [particles, setParticles] = useState([]);
  const hasPlayedRef = useRef(false);

  // Get cycle-based content
  const getCycleContent = () => {
    const tasksTotal = cycleCount * 100;
    
    // Ordinal helper
    const getOrdinal = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    
    // Identity reinforcement based on cycle count
    // Rules: Show at Cycle 2, 3, 5, 6, and 9+. 
    // Cycles 4, 7, 8 are intentionally silent.
    const getIdentityLine = () => {
      if (cycleCount >= 9) return "Relentless.";
      if (cycleCount === 6) return "Relentless.";
      if (cycleCount === 5) return null; // Support line IS the identity
      if (cycleCount === 4) return null; // Silent
      if (cycleCount === 3) return "This is becoming a pattern.";
      if (cycleCount === 2) return "You've done this twice now.";
      return null;
    };
    
    if (cycleCount === 1) {
      return {
        title: "100 Complete",
        subhead: "You closed the loop",
        support: "You showed up, again and again.\nThat's the difference.",
        micro: "Keep the momentum going.",
        identity: null,
      };
    }
    
    if (cycleCount === 2) {
      return {
        title: "200 Complete",
        subhead: "Second century finished",
        support: "This isn't luck anymore.\nThis is how you work.",
        micro: "The flame grows stronger.",
        identity: getIdentityLine(),
      };
    }
    
    if (cycleCount === 3) {
      return {
        title: "300 Complete",
        subhead: "Third century finished",
        support: "You don't start over.\nYou continue.",
        micro: "Onward.",
        identity: getIdentityLine(),
      };
    }
    
    // Cycle 4 - intentionally silent, no identity reinforcement
    if (cycleCount === 4) {
      return {
        title: "400 Complete",
        subhead: "Fourth century finished",
        support: "The rhythm continues.\nYou know the way.",
        micro: "Onward.",
        identity: null, // Silent - no identity line
      };
    }
    
    // Cycle 5 - key identity confirmation moment
    if (cycleCount === 5) {
      return {
        title: "500 Complete",
        subhead: "Five centuries finished",
        support: "This is who you are.",
        micro: "You keep showing up.",
        identity: null, // Support line IS the identity line
      };
    }
    
    // Cycle 6 - "Relentless" identity moment
    if (cycleCount === 6) {
      return {
        title: "600 Complete",
        subhead: "Six centuries finished",
        support: "This is who you are.",
        micro: "Onward.",
        identity: "Relentless.",
      };
    }
    
    // Cycles 7 & 8 - silent, no modal shown (handled in AppPage.jsx)
    // This case shouldn't be reached if AppPage blocks 7 & 8
    if (cycleCount === 7 || cycleCount === 8) {
      return null; // No content - modal shouldn't show
    }
    
    // Cycle 9 - Special completion moment: "Nine completes the cycle"
    if (cycleCount === 9) {
      return {
        title: "900 Complete",
        subhead: "Nine centuries finished",
        support: "Nine completes the cycle",
        micro: "you kept the flame going",
        identity: null, // Support line carries the meaning
        isCycle9: true, // Flag for enhanced animations
      };
    }
    
    // Cycle 10+ - continue with "Relentless"
    return {
      title: `${tasksTotal} Complete`,
      subhead: `${getOrdinal(cycleCount)} century finished`,
      support: "This is who you are.",
      micro: "Onward.",
      identity: getIdentityLine(),
    };
  };
  
  const content = getCycleContent();
  const isCycle9 = cycleCount === 9;

  useEffect(() => {
    if (isOpen && !hasPlayedRef.current) {
      hasPlayedRef.current = true;
      setParticles(generateCompletionParticles(isCycle9));
      
      // Delayed sound and haptic for cinematic arrival
      // Cycle 9 has slightly longer delay for more presence
      const delay = isCycle9 ? 400 : 350;
      setTimeout(() => {
        playResolutionSound(isCycle9);
        triggerClosureHaptic(isCycle9);
      }, delay);
    }
    
    if (!isOpen) {
      hasPlayedRef.current = false;
    }
  }, [isOpen, isCycle9]);

  const handleDismiss = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(35);
    }
    // Trigger the cycle reset before closing
    if (onCycleReset) {
      onCycleReset();
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - gentle dim, slightly more for cycle 9 */}
          <motion.div
            className={`fixed inset-0 backdrop-blur-sm z-50 ${isCycle9 ? 'bg-black/50' : 'bg-black/45'}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: isCycle9 ? 0.45 : 0.35 }}
            onClick={handleDismiss}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative bg-[#FFFAF5] rounded-3xl p-10 max-w-sm mx-4 pointer-events-auto"
              style={{
                // Cycle 9: More presence with enhanced shadow
                boxShadow: isCycle9 
                  ? '0 35px 90px rgba(0,0,0,0.22), 0 14px 40px rgba(249,115,22,0.14)'
                  : '0 30px 80px rgba(0,0,0,0.2), 0 12px 35px rgba(249,115,22,0.1)',
              }}
              initial={{ opacity: 0, y: isCycle9 ? 32 : 28, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 14, scale: 0.96 }}
              transition={{ duration: isCycle9 ? 0.45 : 0.4, ease: [0.25, 0.1, 0.25, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Particles - drift outward slowly, fade quickly */}
              <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
                {particles.map((particle) => (
                  <motion.div
                    key={particle.id}
                    className="absolute rounded-full"
                    style={{
                      width: particle.size,
                      height: particle.size,
                      left: '50%',
                      top: '28%',
                      backgroundColor: particle.color,
                    }}
                    initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
                    animate={{
                      opacity: [0, 0.7, 0.4, 0],
                      scale: [0, 1.15, 0.85, 0.2],
                      x: Math.cos(particle.angle) * particle.distance,
                      y: Math.sin(particle.angle) * particle.distance,
                    }}
                    transition={{
                      duration: particle.duration,
                      delay: particle.delay,
                      ease: [0.1, 0.15, 0.25, 1],
                    }}
                  />
                ))}
              </div>

              {/* Icon with ambient glow and breathing effect */}
              <div className="relative flex justify-center mb-8">
                {/* Wider ambient glow behind icon - enhanced for cycle 9 */}
                <motion.div
                  className={isCycle9 ? "absolute w-40 h-40 rounded-full" : "absolute w-36 h-36 rounded-full"}
                  style={{
                    background: isCycle9
                      ? 'radial-gradient(circle, rgba(249, 115, 22, 0.22) 0%, rgba(239, 68, 68, 0.12) 40%, transparent 70%)'
                      : 'radial-gradient(circle, rgba(249, 115, 22, 0.18) 0%, rgba(239, 68, 68, 0.1) 35%, transparent 65%)',
                  }}
                  initial={{ scale: 0.4, opacity: 0 }}
                  animate={{ 
                    scale: isCycle9 ? [0.4, 1.8, 1.5] : [0.4, 1.6, 1.4],
                    opacity: isCycle9 ? [0, 0.9, 0.6] : [0, 0.85, 0.55]
                  }}
                  transition={{ 
                    duration: isCycle9 ? 1.6 : 1.4, 
                    ease: [0.25, 0.1, 0.3, 1],
                  }}
                />
                
                {/* Wide ripple - Cycle 9: smoother, more symmetrical, feels like a full loop */}
                <motion.div
                  className="absolute w-28 h-28 rounded-full"
                  style={{
                    border: isCycle9 ? '1.5px solid rgba(249, 115, 22, 0.22)' : '1.5px solid rgba(249, 115, 22, 0.18)',
                  }}
                  animate={{
                    scale: isCycle9 ? [1, 2.8, 3.2, 3.4] : [1, 3, 3.5],
                    opacity: isCycle9 ? [0.5, 0.3, 0.15, 0] : [0.45, 0.2, 0],
                  }}
                  transition={{
                    duration: isCycle9 ? 3.2 : 2.8,
                    repeat: isCycle9 ? 1 : 1,
                    ease: isCycle9 ? [0.15, 0.1, 0.25, 1] : [0.2, 0.1, 0.3, 1],
                  }}
                />
                
                {/* Secondary ripple */}
                <motion.div
                  className="absolute w-28 h-28 rounded-full"
                  style={{
                    border: isCycle9 ? '1px solid rgba(249, 115, 22, 0.15)' : '1px solid rgba(249, 115, 22, 0.12)',
                  }}
                  animate={{
                    scale: isCycle9 ? [1, 2.2, 2.6, 2.8] : [1, 2.5, 3],
                    opacity: isCycle9 ? [0.4, 0.2, 0.1, 0] : [0.35, 0.15, 0],
                  }}
                  transition={{
                    duration: isCycle9 ? 2.6 : 2.2,
                    delay: isCycle9 ? 0.6 : 0.5,
                    repeat: 1,
                    ease: [0.2, 0.1, 0.3, 1],
                  }}
                />
                
                {/* Flame icon - most refined state with breathing effect */}
                <motion.div
                  className="relative z-10 rounded-full flex items-center justify-center"
                  style={{
                    width: 76,
                    height: 76,
                    background: 'linear-gradient(135deg, #F97316 0%, #EA580C 35%, #DC2626 100%)',
                    boxShadow: '0 8px 30px rgba(249, 115, 22, 0.55), 0 0 50px rgba(249, 115, 22, 0.25)',
                  }}
                  initial={{ scale: 0.8, opacity: 0.75 }}
                  animate={{ 
                    scale: [1, 1.04, 1], // Subtle breathing
                    opacity: 1 
                  }}
                  transition={{ 
                    scale: {
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    },
                    opacity: { duration: 0.5, delay: 0.25 }
                  }}
                >
                  <Flame 
                    className="text-white" 
                    size={34} 
                    strokeWidth={2.5} 
                    fill="rgba(255,255,255,0.4)" 
                  />
                </motion.div>
              </div>

              {/* Text content */}
              <div className="text-center relative z-10">
                <motion.h2
                  className="text-2xl font-bold text-[#1A1A1C] mb-1.5"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.22 }}
                >
                  {content.title}
                </motion.h2>
                
                <motion.p
                  className="text-base text-[#6B6B6B] mb-3"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.28 }}
                >
                  {content.subhead}
                </motion.p>
                
                <motion.p
                  className="text-sm text-[#8B8B8B] mb-2 leading-relaxed whitespace-pre-line"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.34 }}
                >
                  {content.support}
                </motion.p>
                
                {/* Identity reinforcement - only show at certain milestones */}
                {content.identity && (
                  <motion.p
                    className="text-xs text-[#A5A5A5] mb-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.55 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    {content.identity}
                  </motion.p>
                )}
                
                <motion.p
                  className="text-xs text-[#B5B5B5] mb-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.65 }}
                  transition={{ duration: 0.45, delay: 0.42 }}
                >
                  {content.micro}
                </motion.p>

                {/* Primary button */}
                <motion.button
                  className="relative px-9 py-4 rounded-full text-white font-semibold text-base overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #F97316 0%, #EA580C 45%, #DC2626 100%)',
                  }}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.38 }}
                  whileHover={{ 
                    scale: 1.02,
                    boxShadow: '0 10px 35px rgba(249, 115, 22, 0.55)',
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDismiss}
                  data-testid="cycle-completion-continue-btn"
                >
                  <span className="relative z-10">Continue 🔥</span>
                  
                  {/* Subtle inner glow */}
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: 'radial-gradient(circle at center, rgba(255,255,255,0.28) 0%, transparent 55%)',
                    }}
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.25 }}
                  />
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CycleCompletionModal;
